import axios from "axios";
import { toast } from "react-toastify";
import { fetchMedicalServices, isHospitalOrderNo, serviceGroupOf } from "./risIntegration";

// LIS hand-off for lab tests — when the phlebotomist sends a sample to the
// in-house lab (lab-technician/IpdLabOrders).
//
// The frontend only sends our own data to `lis/patient_registration/`. The
// backend picks the facility's LIS partner and that partner's *verified* code
// for the test (LIS Mapping screen), and the partner's adapter builds the
// vendor payload — so no LIS company, code or account lives in this file.
// The LIS later POSTs the report URL back, which lands in test_report_url.
//
// One call per test: PatientLaboratoryTest holds a single medical_service_id and
// the callback returns one report URL per patientCode, so a combined call could
// link only one of its tests back to its order.

const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL || "";

const isLabService = (svc) => serviceGroupOf(svc) === "LABORATORY";

const listFee = (svc) => {
  const fee = Number(svc.fee);
  return Number.isFinite(fee) ? fee : 0;
};

// `gross` is the charge before discount, `net` what the patient pays for it.
// Only sent once charged — OPD after payment, IPD on credit — so it goes as
// settled, like ISPAID "1" on the RIS side.
const buildPayload = ({ test, patientId, patientType, careContextId, referBy }) => {
  const { svc, gross, net, appointmentId, orderNo } = test;
  return {
    patient_id: patientId,
    ...(appointmentId ? { appointment_id: String(appointmentId) } : {}),
    carecontext_id: careContextId ? String(careContextId) : "",
    // Stored as the PatientLaboratoryTest order_no, so the test and its order share one id.
    order_no: isHospitalOrderNo(orderNo) ? String(orderNo).trim() : "",
    medical_service_id: String(svc.medical_service_id || ""),
    medical_service_name: svc.name || "",
    patient_type: patientType,
    referring_doctor: referBy,
    rate: gross,
    net,
  };
};

// Never throws — failures surface as toasts only.
async function sendLabTestsToLis({ tests, headers, ...context }) {
  const results = await Promise.all(
    tests.map(async (test) => {
      const name = test.svc.name || "";
      try {
        const res = await axios.post(
          `${REACT_APP_BACKEND_ROUTE_URL}lis/patient_registration/`,
          buildPayload({ test, ...context }),
          { headers },
        );
        return res?.data?.status === "Success"
          ? { ok: true, name }
          : { ok: false, name, message: res?.data?.message };
      } catch (err) {
        return { ok: false, name, message: err?.response?.data?.message || err?.message };
      }
    }),
  );

  const sent = results.filter((r) => r.ok);
  if (sent.length) {
    toast.success(`Lab order sent to LIS: ${sent.map((r) => r.name).join(", ")}`);
  }
  results
    .filter((r) => !r.ok)
    .forEach((r) => {
      console.error("LIS registration failed:", r.name, r.message);
      toast.warn(`LIS registration failed for ${r.name}${r.message ? `: ${r.message}` : ""}`);
    });
}

/** Every PatientLaboratoryTest row of a patient (UHID), newest first. */
export async function fetchPatientLisRecords(patientId, headers) {
  const res = await axios.get(
    `${REACT_APP_BACKEND_ROUTE_URL}lis/patient_lab_records/${encodeURIComponent(patientId)}/`,
    { headers },
  );
  return Array.isArray(res?.data?.data) ? res.data.data : [];
}

/**
 * Registers the Laboratory tests of a sample with the LIS once the
 * phlebotomist has sent it to the in-house lab — IPD, OPD and Emergency alike.
 * Each test goes at its service master rate: the lab board does not know the
 * bill, so no discount is sent. Never throws — failures surface as toasts only.
 *
 * @param {object} args
 * @param {string} args.patientId      UHID (PatientRecord.patient_id, e.g. "PAT-1314")
 * @param {string} args.patientType    "IPD" / "OPD" / "EMERGENCY", as the lab board has it
 * @param {Array}  args.serviceIds     the sample's MedicalService ids (UUID or integer PK)
 * @param {object} args.orderNos       { [serviceId]: LAB- order number } for those ids
 * @param {string} args.careContextId  visit / admission care context
 * @param {string} args.appointmentId  OPD appointment, if any
 * @param {string} args.doctorName     ordering doctor (ReferBy / ReportingDoctor)
 * @param {object} args.headers        request headers incl. Authorization
 */
export async function sendLabSampleToLis({
  patientId,
  patientType,
  serviceIds,
  orderNos = {},
  careContextId,
  appointmentId,
  doctorName,
  headers,
}) {
  if (!patientId || !serviceIds.length) return;

  let services;
  try {
    services = await fetchMedicalServices(serviceIds, headers);
  } catch (err) {
    console.error("LIS service lookup failed:", err);
    toast.warn("Could not send lab order to LIS.");
    return;
  }

  const tests = services.filter(isLabService).map((svc) => {
    const fee = listFee(svc);
    // The board's id may be the integer PK (IPD) or the UUID (OPD).
    const orderNo = orderNos[String(svc.id)] ?? orderNos[String(svc.medical_service_id)];
    return { svc, gross: fee, net: fee, appointmentId, orderNo };
  });
  if (!tests.length) return;

  return sendLabTestsToLis({
    tests,
    patientId,
    patientType,
    careContextId,
    referBy: doctorName,
    headers,
  });
}
